/** Push web: avisa tarea nueva y abre la bandeja (design.md §22.6). */
import { mobileApiClient } from './mobileApiClient';

export type PushSubscriptionBody = {
  endpoint: string;
  p256dh: string;
  auth: string;
  user_agent?: string;
};

export type PushState = 'unsupported' | 'denied' | 'inactive' | 'subscribed';

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

function waitForRegistration(timeoutMs = 5_000): Promise<ServiceWorkerRegistration> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Service worker no disponible')),
      timeoutMs,
    );
    navigator.serviceWorker.ready.then(
      (registration) => {
        clearTimeout(timer);
        resolve(registration);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export const pushService = {
  supported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      typeof Notification !== 'undefined'
    );
  },

  /** Estado real del dispositivo: sin suscripción, activa o permiso denegado. */
  async state(): Promise<PushState> {
    if (!this.supported()) return 'unsupported';
    if ((await currentSubscription()) !== null) return 'subscribed';
    if (Notification.permission === 'denied') return 'denied';
    return 'inactive';
  },

  async vapidPublicKey(): Promise<string> {
    const { data } = await mobileApiClient.get('/api/v1/push/vapid-key');
    return data.public_key as string;
  },

  /** Registra la suscripción del dispositivo contra la membresía activa. */
  async subscribe(): Promise<void> {
    if (!this.supported()) throw new Error('Push no soportado en este dispositivo');
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('Permiso de notificación denegado');
    const registration = await waitForRegistration();
    let subscription = await registration.pushManager.getSubscription();
    if (subscription === null) {
      const publicKey = await this.vapidPublicKey();
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    const keys = subscription.toJSON().keys ?? {};
    await mobileApiClient.post('/api/v1/push/subscriptions', {
      endpoint: subscription.endpoint,
      p256dh: keys['p256dh'] ?? '',
      auth: keys['auth'] ?? '',
      user_agent: navigator.userAgent,
    } satisfies PushSubscriptionBody);
  },

  /** Baja la suscripción del servidor y del navegador en este dispositivo. */
  async unsubscribeCurrent(): Promise<void> {
    if (!this.supported()) return;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    try {
      await mobileApiClient.delete('/api/v1/push/subscriptions/by-endpoint', {
        data: { endpoint },
      });
    } catch {
      // La baja local manda: el servidor retirará el endpoint al recibir 410.
    }
    await subscription.unsubscribe();
  },
};
